import { useContext } from 'react';
import type { Dispatch, SetStateAction, KeyboardEvent, ReactNode } from 'react';
import { useTheme, Tooltip, Stack, Grid, Typography, CircularProgress, Box } from '@mui/material';
import { shouldDisableTemplate } from './utils';
import { CareplanTemplate } from 'shared-components/images';
import { AddCircleOutline } from '@mui/icons-material';
import { CareplanListFilter } from '../Interface';
import { TenantContext } from 'op-contexts';

interface TemplateTableColumnProps {
  title: string;
  options: string[];
  id: string;
  onClick: (value: string) => void;
  selectedValue: string;
}

interface CareplanColumnProps {
  title: string;
  options: CareplanListFilter[];
  id: string;
  onClick: (id: number, value: string) => void;
  treatingDepartment: string;
  loadingTemplateId?: number | null;
}

interface Props {
  searchTerm: string;
  siteFilter: string;
  siteFilterOptions: string[];
  setSiteFilter: Dispatch<SetStateAction<string>>;
  doseFilter: string;
  doseFilterOptions: string[];
  setDoseFilter: Dispatch<SetStateAction<string>>;
  techniqueFilter: string;
  techniqueFilterOptions: string[];
  setTechniqueFilter: Dispatch<SetStateAction<string>>;
  treatingDepartment: string;
  setCareplan: (id: number, name: string) => void;
  careplanOptions: CareplanListFilter[];
  setCareplanOptions: Dispatch<SetStateAction<CareplanListFilter[]>>;
  loadingTemplateId?: number | null;
}

const ColumnTitle = ({ title }: { title: string | ReactNode }) => {
  const theme = useTheme();
  return (
    <Typography
      padding={2}
      sx={{
        backgroundColor: theme.palette.grey[100],
        borderRadius: '4px 4px 0 0',
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
      }}
      variant="subtitle1">
      {title}
    </Typography>
  );
};

const CareplanColumn = ({
  title,
  options,
  id,
  onClick,
  treatingDepartment,
  loadingTemplateId = null,
}: CareplanColumnProps) => {
  const theme = useTheme();
  return (
    <Stack width="100%" sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: '4px' }}>
      <ColumnTitle
        title={
          <Stack direction="row" alignItems="center">
            <CareplanTemplate />
            {title}
          </Stack>
        }
      />
      <Stack data-testid={id} sx={{ overflowY: 'auto', height: '570px' }}>
        {options?.map((option, index) => {
          const disabled = shouldDisableTemplate(option, treatingDepartment) || false;
          const isLoading = loadingTemplateId === option.id;
          return (
            <Tooltip
              key={`tooltip-${index}`}
              arrow
              title={
                disabled && 'This template cannot be used for the selected treating department on the previous page.'
              }>
              <Stack
                id={`template-${index}`}
                data-testid={option.name}
                key={`${option.name + option.id}`}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                padding={2}
                sx={{
                  cursor: 'pointer',
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                  backgroundColor: 'transparent',
                  ':hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                role="button"
                aria-disabled={disabled || isLoading || undefined}
                tabIndex={disabled || isLoading ? -1 : 0}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>): void => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!disabled && !isLoading) onClick(option?.id, option?.name);
                  }
                }}
                onClick={(): void => {
                  if (disabled || isLoading) return;
                  onClick(option?.id, option?.name);
                }}>
                <Typography
                  sx={{
                    color: disabled || isLoading ? theme.palette.grey[600] : theme.palette.text.primary,
                  }}
                  variant="body1">
                  {option.name}
                </Typography>
                {!disabled && (
                  <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isLoading ? <CircularProgress size={20} /> : <AddCircleOutline color="primary" />}
                  </Box>
                )}
              </Stack>
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );
};

const TemplateTableColumn = ({ title, options, id, onClick, selectedValue = '' }: TemplateTableColumnProps) => {
  const theme = useTheme();
  return (
    <Stack width="100%" sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: '4px' }}>
      <ColumnTitle title={title} />
      <Stack data-testid={id} sx={{ overflowY: 'auto', height: '570px' }}>
        {options?.map((option, index) => {
          const active = selectedValue === option;
          return (
            <Stack
              id={`item-${index}`}
              data-testid={option}
              key={option}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              padding={2}
              sx={{
                cursor: 'pointer',
                borderBottom: `1px solid ${theme.palette.grey[100]}`,
                borderLeft: active ? `4px solid ${theme.palette.primary.main}` : 'none',
                backgroundColor: active ? theme.palette.secondary.light : 'transparent',
                ':hover': {
                  backgroundColor: active ? theme.palette.secondary.light : theme.palette.action.hover,
                },
              }}
              role="button"
              aria-pressed={active}
              tabIndex={0}
              onKeyDown={(e: KeyboardEvent<HTMLDivElement>): void => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick(option);
                }
              }}
              onClick={(): void => {
                onClick(option);
              }}>
              <Typography variant="body1">{option}</Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

const TemplateSelectionTable = ({
  searchTerm,
  siteFilter,
  siteFilterOptions,
  doseFilter,
  doseFilterOptions,
  techniqueFilter,
  techniqueFilterOptions,
  setSiteFilter,
  setDoseFilter,
  setTechniqueFilter,
  treatingDepartment,
  setCareplan,
  careplanOptions,
  setCareplanOptions,
  loadingTemplateId = null,
}: Props): ReactNode => {
  const { doseUnit } = useContext(TenantContext);
  return (
    <Grid container width="100%" columns={14} sx={{ minHeight: 0 }}>
      <Grid item xs={4} paddingRight={2}>
        <TemplateTableColumn
          id="site-filter-column"
          title="Site Filter"
          selectedValue={siteFilter}
          options={siteFilterOptions}
          onClick={(newSiteFilter: string) => {
            setCareplanOptions([]);
            // clear on selecting the same site filter
            setSiteFilter((prevSiteFilter: string) => (prevSiteFilter === newSiteFilter ? '' : newSiteFilter));
          }}
        />
      </Grid>
      <Grid item xs={3} paddingRight={2}>
        <TemplateTableColumn
          id="dose-filter-column"
          title={`Dose Filter (${doseUnit})`}
          selectedValue={doseFilter}
          options={doseFilterOptions}
          onClick={(newDoseFilter: string) => {
            setCareplanOptions([]);
            // clear on selecting the same dose filter
            setDoseFilter((prevDoseFilter: string) => (prevDoseFilter === newDoseFilter ? '' : newDoseFilter));
          }}
        />
      </Grid>
      <Grid item xs={3} paddingRight={2}>
        <TemplateTableColumn
          id="technique-filter-column"
          title="Technique Filter"
          selectedValue={techniqueFilter}
          options={techniqueFilterOptions}
          onClick={(newTechniqueFilter: string) => {
            setCareplanOptions([]);
            // clear on selecting the same technique filter
            setTechniqueFilter((prevTechniqueFilter: string) =>
              prevTechniqueFilter === newTechniqueFilter ? '' : newTechniqueFilter,
            );
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <CareplanColumn
          id="careplan-column"
          title="Careplan Template"
          options={techniqueFilter || doseFilter || siteFilter || searchTerm ? careplanOptions : []}
          onClick={(id: number, newCareplan: string) => {
            setCareplan(id, newCareplan);
          }}
          treatingDepartment={treatingDepartment}
          loadingTemplateId={loadingTemplateId}
        />
      </Grid>
    </Grid>
  );
};

export default TemplateSelectionTable;
